import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import GridViewIcon from '@mui/icons-material/GridView';
import LockIcon from '@mui/icons-material/Lock';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useRuntimeChannel } from '../../contexts/ConsoleContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { formatCodecPair } from '../../utils/codecLabels';
import {
  cancelMediaBoxUpload,

  cancelMediaBoxProcessing,
  deleteMediaBoxAsset,
  fetchMediaBoxThumbnail,
  getMediaBoxUploadState,
  getMediaBoxAssets,
  getMediaBoxPlayback,
  getMediaBoxSummary,
  MediaBoxAsset,
  MediaBoxConfig,
  MediaBoxSummary,
  MediaBoxUploadProfile,
  MediaBoxUploadState,
  renameMediaBoxAsset,

  reprocessMediaBoxAsset,
  subscribeMediaBoxUploads,
  updateMediaBoxPlayback,
  uploadMediaBoxAsset,
} from '../../services/mediaboxService';

const v = (name: string) => `var(${name})`;

const bytesToGb = (value: number) => `${(value / (1024 ** 3)).toFixed(2)} GB`;

const isSupportedMediaFile = (file: File) => /\.(mp4|mkv|mov)$/i.test(file.name);

const defaultPlayback: MediaBoxConfig = {
  playbackEnabled: false,
  playAllAssets: true,
  loopPlayback: false,
  backupEnabled: true,
  backupThresholdSecs: 2,
  uploadProfile: 'h264_opus',
  selectedAssetIds: [],
};

type ViewMode = 'list' | 'grid';

const INFO_SURFACE = v('--info-light');
const READY_SURFACE = v('--success-light');
const ERROR_SURFACE = v('--danger-light');
const PROCESSING_SURFACE = v('--warning-light');
const ROW_SELECTED_SURFACE = v('--accent-light');
const ROW_HOVER_SURFACE = v('--bg-card-hover');
const PRIMARY_SURFACE = v('--primary-light');

const getAssetProgress = (asset: MediaBoxAsset) => {
  if (typeof asset.normProgressPct === 'number') return asset.normProgressPct;
  if (asset.normStatus === 'READY') return 100;
  return 0;
};

const getStatusTone = (asset: MediaBoxAsset) => {
  const status = String(asset.normStatus || '').toUpperCase();
  if (status === 'READY') return { label: 'ready', color: 'success.main', bg: READY_SURFACE };
  if (status === 'ERROR') return { label: 'error', color: 'error.main', bg: ERROR_SURFACE };
  return { label: 'processing', color: 'warning.main', bg: PROCESSING_SURFACE };
};

const formatRuntimeStopMessage = (lastError?: string | null) => {
  const raw = String(lastError || '').trim();
  const normalized = raw.toLowerCase();
  const isPublishAuthFailure = normalized.includes('401')
    || normalized.includes('unauthorized')
    || normalized.includes('method announce failed');

  if (isPublishAuthFailure) {
    return 'Playback blocked: this channel is not authorized to publish because the account or subscription is inactive. Please resolve billing or request a grace period, then re-arm MediaBox.';
  }

  return `Playback stopped - ${raw}`;
};

const assetsSignature = (items: MediaBoxAsset[]) => items.map((asset) => [
  asset.id,
  asset.label,
  asset.updatedAt,
  asset.normStatus,
  asset.normStage,
  asset.normProgressPct,
  asset.isCurrent ? 1 : 0,
  asset.isNext ? 1 : 0,
].join(':')).join('|');

const playbackSignature = (config: MediaBoxConfig) => [
  config.playbackEnabled ? 1 : 0,
  config.playAllAssets ? 1 : 0,
  config.loopPlayback ? 1 : 0,
  config.backupEnabled ? 1 : 0,
  config.backupThresholdSecs,
  config.uploadProfile,
  config.selectedAssetIds.join(','),
  config.runtime?.running ? 1 : 0,
  config.runtime?.mode || '',
  config.runtime?.armed ? 1 : 0,
  config.runtime?.activeAssetCount || 0,
  config.runtime?.currentAssetId || '',
  config.runtime?.nextAssetId || '',
  config.runtime?.lastError || '',
].join('|');

const summarySignature = (summary: MediaBoxSummary | null) => summary ? [
  summary.channelId,
  summary.addonId,
  summary.tierCode,
  summary.storageBytes,
  summary.usedBytes,
  summary.remainingBytes,
  summary.assetCount,
].join('|') : '';

const LazyThumbnail = React.memo(({
  assetId,
  label,
  updatedAt,
  src,
  onVisible,
  iconSize = 20,
}: {
  assetId: number;
  label: string;
  updatedAt: string;
  src?: string;
  onVisible: (assetId: number) => void;
  iconSize?: number;
}) => {
  const hostRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (typeof IntersectionObserver === 'undefined') {
      onVisible(assetId);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onVisible(assetId);
        observer.disconnect();
      }
    }, { rootMargin: '160px' });
    observer.observe(host);
    return () => observer.disconnect();
  }, [assetId, onVisible, updatedAt]);

  return (
    <Box ref={hostRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {src ? (
        <Box component="img" src={src} alt={label} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <PlayCircleOutlineIcon sx={{ color: 'text.secondary', fontSize: iconSize }} />
      )}
    </Box>
  );
});

export const MediaStoreTab: React.FC = () => {
  const { activeChannel } = useRuntimeChannel();
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<MediaBoxSummary | null>(null);
  const [assets, setAssets] = useState<MediaBoxAsset[]>([]);
  const [playback, setPlayback] = useState<MediaBoxConfig>(defaultPlayback);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<MediaBoxUploadState | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);
  const [workflowHelpAnchor, setWorkflowHelpAnchor] = useState<HTMLElement | null>(null);
  const [renameDrafts, setRenameDrafts] = useState<Record<number, string>>({});
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<number, string>>({});
  const [savingNameId, setSavingNameId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = window.localStorage.getItem('mediabox:viewMode');
    return stored === 'grid' ? 'grid' : 'list';
  });
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const pendingPlaybackRef = React.useRef<MediaBoxConfig | null>(null);
  const playbackSaveRevisionRef = React.useRef(0);
  const assetsRef = React.useRef<MediaBoxAsset[]>([]);
  const loadInFlightRef = React.useRef(false);
  const pollingBlockedRef = React.useRef(false);
  const thumbnailChannelRef = React.useRef<string>('');
  const thumbnailCacheRef = React.useRef<Record<number, { key: string; url: string }>>({});
  const thumbnailInFlightRef = React.useRef(new Set<string>());
  const dragDepthRef = React.useRef(0);
  const channelRouteId = String(activeChannel?.channelHashId || activeChannel?.hashId || activeChannel?.id || '');
  thumbnailChannelRef.current = channelRouteId;
  const showSnackbarRef = React.useRef(showSnackbar);

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const load = useCallback(async (options?: { silent?: boolean; background?: boolean; refreshSummary?: boolean }) => {
    if (!channelRouteId) return;
    if (options?.background && pollingBlockedRef.current) return;
    if (options?.background && loadInFlightRef.current) return;
    if (!options?.background) pollingBlockedRef.current = false;
    loadInFlightRef.current = true;
    if (!options?.background) {
      setWorkflowHelpAnchor(null);
      setLoading(true);
    }
    if (!options?.silent) setDisabledMessage(null);
    try {
      const [summaryData, assetsData, playbackData] = await Promise.all([
        !options?.background || options?.refreshSummary ? getMediaBoxSummary(channelRouteId) : Promise.resolve(null),
        getMediaBoxAssets(channelRouteId),
        getMediaBoxPlayback(channelRouteId),
      ]);
      pollingBlockedRef.current = false;
      if (summaryData) setSummary((prev) => summarySignature(prev) === summarySignature(summaryData) ? prev : summaryData);
      assetsRef.current = assetsData;
      setAssets((prev) => assetsSignature(prev) === assetsSignature(assetsData) ? prev : assetsData);
      setPlayback((prev) => {
        const next = pendingPlaybackRef.current ? { ...playbackData, ...pendingPlaybackRef.current } : playbackData;
        return playbackSignature(prev) === playbackSignature(next) ? prev : next;
      });
      setRenameDrafts((prev) => {
        const next = { ...prev };
        let changed = false;
        assetsData.forEach((asset) => {
          if (!(asset.id in next)) {
            next[asset.id] = asset.label;
            changed = true;
          }
        });
        Object.keys(next).forEach((key) => {
          if (!assetsData.some((asset) => asset.id === Number(key))) {
            delete next[Number(key)];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || 'Failed to load MediaBox.';
      if (status === 409) {
        setDisabledMessage(message);
        setSummary(null);
        setAssets([]);
        setPlayback(defaultPlayback);
      } else if (status === 403) {
        pollingBlockedRef.current = true;
        if (!options?.background) setDisabledMessage(message);
      } else if (!options?.silent) {
        showSnackbarRef.current(message, 'error');
      }
    } finally {
      loadInFlightRef.current = false;
      if (!options?.background) setLoading(false);
    }
  }, [channelRouteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!channelRouteId) return;
    let timer: number | null = null;
    const stop = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (timer === null && !document.hidden) {
        timer = window.setInterval(() => {
          void load({ silent: true, background: true });
        }, 5000);
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      void load({ silent: true, background: true });
      start();
    };
    start();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [channelRouteId, load]);

  useEffect(() => {
    window.localStorage.setItem('mediabox:viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!channelRouteId) return;
    const sync = () => setUploadState(getMediaBoxUploadState(channelRouteId));
    sync();
    const unsubscribe = subscribeMediaBoxUploads(sync);
    return () => {
      unsubscribe();
    };
  }, [channelRouteId]);

  const clearThumbnailCache = useCallback(() => {
    Object.values(thumbnailCacheRef.current).forEach(({ url }) => URL.revokeObjectURL(url));
    thumbnailCacheRef.current = {};
    thumbnailInFlightRef.current.clear();
    setThumbnailUrls({});
  }, []);

  useEffect(() => {
    const channelPrefix = channelRouteId ? `${channelRouteId}:` : '';
    let changed = false;
    Object.entries(thumbnailCacheRef.current).forEach(([rawId, cached]) => {
      if (channelPrefix && cached.key.startsWith(channelPrefix)) return;
      URL.revokeObjectURL(cached.url);
      delete thumbnailCacheRef.current[Number(rawId)];
      changed = true;
    });
    thumbnailInFlightRef.current.clear();
    if (changed) {
      setThumbnailUrls(Object.fromEntries(
        Object.entries(thumbnailCacheRef.current).map(([rawId, cached]) => [Number(rawId), cached.url]),
      ));
    }
  }, [channelRouteId]);

  useEffect(() => () => clearThumbnailCache(), [clearThumbnailCache]);

  useEffect(() => {
    assetsRef.current = assets;
    const activeIds = new Set(assets.map((asset) => asset.id));
    let urlsChanged = false;
    Object.entries(thumbnailCacheRef.current).forEach(([rawId, cached]) => {
      const id = Number(rawId);
      if (activeIds.has(id)) return;
      URL.revokeObjectURL(cached.url);
      delete thumbnailCacheRef.current[id];
      urlsChanged = true;
    });
    if (urlsChanged) {
      setThumbnailUrls((prev) => Object.fromEntries(
        Object.entries(prev).filter(([rawId]) => activeIds.has(Number(rawId))),
      ));
    }
  }, [assets]);

  const ensureThumbnail = useCallback(async (assetId: number) => {
    if (!channelRouteId) return;
    const asset = assetsRef.current.find((item) => item.id === assetId);
    if (!asset) return;
    const channelKey = channelRouteId;
    const cacheKey = `${channelKey}:${asset.id}:${asset.updatedAt}`;
    const cached = thumbnailCacheRef.current[asset.id];
    if (cached?.key === cacheKey || thumbnailInFlightRef.current.has(cacheKey)) return;

    thumbnailInFlightRef.current.add(cacheKey);
    try {
      const blob = await fetchMediaBoxThumbnail(channelRouteId, asset.id);
      const url = URL.createObjectURL(blob);
      if (thumbnailChannelRef.current !== channelKey) {
        URL.revokeObjectURL(url);
        return;
      }
      const previous = thumbnailCacheRef.current[asset.id];
      thumbnailCacheRef.current[asset.id] = { key: cacheKey, url };
      setThumbnailUrls((prev) => prev[asset.id] === url ? prev : { ...prev, [asset.id]: url });
      if (previous?.url && previous.url !== url) URL.revokeObjectURL(previous.url);
    } catch {
      // Ignore missing thumbnails.
    } finally {
      thumbnailInFlightRef.current.delete(cacheKey);
    }
  }, [channelRouteId]);

  const usagePercent = useMemo(() => {
    if (!summary || summary.storageBytes <= 0) return 0;
    return Math.min(100, Math.round((summary.usedBytes / summary.storageBytes) * 100));
  }, [summary]);

  const runtimeAlert = useMemo(() => {
    if (playback.runtime?.running) {
      return {
        severity: 'success' as const,
        message: `${playback.runtime.mode === 'backup' ? 'Backup live' : 'Manual live'} · ${playback.runtime.activeAssetCount} armed`,
      };
    }
    if (playback.runtime?.armed) {
      return {
        severity: 'info' as const,
        message:
          playback.runtime.mode === 'backup-armed'
            ? 'Backup armed · waiting for live loss'
            : 'Manual armed · live source has priority',
      };
    }
    if (playback.runtime?.lastError) {
      return {
        severity: 'error' as const,
        message: formatRuntimeStopMessage(playback.runtime.lastError),
      };
    }
    return null;
  }, [playback.runtime]);

  const processingAssets = useMemo(() => assets.filter((asset) => asset.normStatus === 'PENDING'), [assets]);
  const readinessProgress = useMemo(() => {
    if (processingAssets.length === 0) return 0;
    const totals = processingAssets.map((asset) => getAssetProgress(asset));
    return Math.round(totals.reduce((sum, value) => sum + value, 0) / totals.length);
  }, [processingAssets]);

  const selectedCount = playback.playAllAssets ? assets.length : playback.selectedAssetIds.length;
  const canUploadMore = assets.length < 4 && !uploading && !uploadState?.active;

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedAssetId) || null,
    [assets, selectedAssetId],
  );

  const handleUploadFiles = async (selectedFiles: File[]) => {
    if (!channelRouteId || selectedFiles.length === 0) return;
    const availableSlots = Math.max(0, 4 - assets.length);
    if (availableSlots === 0) {
      showSnackbar('MediaBox cannot hold more than four videos. Delete a clip before uploading.', 'warning');
      return;
    }
    if (selectedFiles.length > availableSlots) {
      showSnackbar(`Only ${availableSlots} more clip${availableSlots === 1 ? '' : 's'} can be uploaded to this MediaBox.`, 'warning');
      return;
    }
    const unsupported = selectedFiles.find((file) => !isSupportedMediaFile(file));
    if (unsupported) {
      showSnackbar(`Upload rejected: ${unsupported.name} is not an MP4, MKV, or MOV file.`, 'error');
      return;
    }
    const totalBytes = selectedFiles.reduce((total, file) => total + file.size, 0);
    if (summary && totalBytes > summary.remainingBytes) {
      showSnackbar(`Upload rejected: the selected files total ${bytesToGb(totalBytes)}, but only ${bytesToGb(summary.remainingBytes)} is available.`, 'error');
      return;
    }
    setUploading(true);
    setUploadName(selectedFiles[0].name);
    setUploadPercent(0);
    const createdAssets: MediaBoxAsset[] = [];
    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        setUploadName(selectedFiles.length > 1 ? `${index + 1}/${selectedFiles.length} ${file.name}` : file.name);
        const created = await uploadMediaBoxAsset(channelRouteId, file, undefined, (percent) => {
          setUploadPercent(Math.round(((index + (percent / 100)) / selectedFiles.length) * 100));
        });
        createdAssets.push(created);
        setAssets((prev) => [created, ...prev.filter((asset) => asset.id !== created.id)]);
        setRenameDrafts((prev) => ({ ...prev, [created.id]: created.label }));
        setSelectedAssetId(created.id);
      }
      showSnackbar(`${createdAssets.length} clip${createdAssets.length === 1 ? '' : 's'} uploaded with ${playback.uploadProfile === 'h264_aac' ? 'H264/AAC' : 'H264/Opus'} workflow. Processing has started.`, 'success');
      await load({ silent: true, background: true, refreshSummary: true });
    } catch (error: any) {
      if (error?.code !== 'ERR_CANCELED') {
        const partial = createdAssets.length > 0 ? ` ${createdAssets.length} clip${createdAssets.length === 1 ? ' was' : 's were'} uploaded before the failure.` : '';
        showSnackbar(`${error?.response?.data?.message || 'Upload failed.'}${partial}`, 'error');
      }
      if (createdAssets.length > 0) {
        await load({ silent: true, background: true, refreshSummary: true });
      }
    } finally {
      setUploading(false);
      setUploadPercent(0);
      setUploadName(null);
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    void handleUploadFiles(files);
  };

  const handleRename = async (asset: MediaBoxAsset) => {
    if (!channelRouteId) return;
    const nextLabel = String(renameDrafts[asset.id] || '').trim();
    if (!nextLabel || nextLabel === asset.label) return;
    setSavingNameId(asset.id);
    try {
      await renameMediaBoxAsset(channelRouteId, asset.id, nextLabel);
      showSnackbar('Clip name updated.', 'success');
      await load({ silent: true, background: true });
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Rename failed.', 'error');
    } finally {
      setSavingNameId(null);
    }
  };

  const handleDelete = async (assetId: number) => {
    if (!channelRouteId) return;
    setDeletingId(assetId);
    try {
      await deleteMediaBoxAsset(channelRouteId, assetId);
      if (selectedAssetId === assetId) setSelectedAssetId(null);
      showSnackbar('Clip deleted.', 'success');
      await load({ silent: true, background: true, refreshSummary: true });
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Delete failed.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReprocess = async (assetId: number) => {
    if (!channelRouteId) return;
    setReprocessingId(assetId);
    try {
      await reprocessMediaBoxAsset(channelRouteId, assetId);
      showSnackbar('Clip queued for re-processing.', 'success');
      await load({ silent: true, background: true, refreshSummary: true });
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Could not re-process this clip.', 'error');
    } finally {
      setReprocessingId(null);
    }
  };

  const handleCancelUpload = () => {
    if (!channelRouteId || !uploadState?.active) return;
    if (!window.confirm(`Cancel upload for "${uploadState.fileName}"?`)) return;
    cancelMediaBoxUpload(channelRouteId);
    showSnackbar('Upload cancelled.', 'info');
  };

  const handleCancelProcessing = async () => {
    if (!channelRouteId || processingAssets.length === 0) return;
    if (!window.confirm(`Cancel processing for ${processingAssets.length} clip${processingAssets.length === 1 ? '' : 's'}?`)) return;
    try {
      await Promise.all(processingAssets.map((asset) => cancelMediaBoxProcessing(channelRouteId, asset.id)));
      showSnackbar('Processing cancelled.', 'success');
      await load({ silent: true, background: true, refreshSummary: true });
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Could not cancel processing.', 'error');
    }
  };

  const savePlayback = async (
    nextPlayback: MediaBoxConfig,
    _key?: string,
    options?: { successMessage?: string; suppressSuccess?: boolean },
  ) => {
    if (!channelRouteId) return;
    pendingPlaybackRef.current = nextPlayback;
    const revision = ++playbackSaveRevisionRef.current;
    try {
      await updateMediaBoxPlayback(channelRouteId, nextPlayback);
      if (playbackSaveRevisionRef.current === revision) pendingPlaybackRef.current = null;
      if (!options?.suppressSuccess) {
        showSnackbar(options?.successMessage || 'Playback settings saved.', 'success');
      }
    } catch (error: any) {
      if (playbackSaveRevisionRef.current !== revision) return;
      pendingPlaybackRef.current = null;
      showSnackbar(error?.response?.data?.message || 'Could not save playback settings.', 'error');
      await load({ silent: true, background: true });
    }
  };

  const updateUploadProfile = async (profile: MediaBoxUploadProfile | null) => {
    if (!profile || profile === playback.uploadProfile) return;
    if (assets.length > 0) {
      showSnackbar('Delete every MediaBox clip before changing the audio workflow.', 'warning');
      return;
    }
    const nextPlayback: MediaBoxConfig = { ...playback, uploadProfile: profile };
    setPlayback(nextPlayback);
    await savePlayback(nextPlayback, 'uploadProfile', {
      successMessage: `MediaBox workflow changed to ${profile === 'h264_aac' ? 'H264/AAC' : 'H264/Opus'}.`,
    });
  };

  const buildPlaybackSelection = (
    baseSelectedIds: number[],
    nextPlayAllAssets: boolean,
  ) => {
    if (nextPlayAllAssets) {
      const readyIds = assets.filter((asset) => asset.normStatus === 'READY').map((asset) => asset.id);
      return readyIds;
    }
    const availableIds = new Set(assets.map((asset) => asset.id));
    const unique = Array.from(new Set(baseSelectedIds)).filter((id) => availableIds.has(id));
    return unique;
  };

  const updateAutoArming = async (value: boolean) => {
    const nextPlayback: MediaBoxConfig = {
      ...playback,
      playAllAssets: value,
      loopPlayback: true,
      selectedAssetIds: buildPlaybackSelection(playback.selectedAssetIds, value),
    };
    setPlayback(nextPlayback);
    await savePlayback(nextPlayback, 'playAllAssets', { successMessage: 'Auto arming updated.' });
  };

  const armEnabled = playback.playbackEnabled || playback.backupEnabled;
  const updateAutoBackup = async (enabled: boolean) => {
    if (enabled && !playback.playAllAssets && playback.selectedAssetIds.length === 0) {
      showSnackbar('Select at least one clip or enable Auto Arming before enabling Auto Backup.', 'warning');
      return;
    }
    const nextPlayback: MediaBoxConfig = {
      ...playback,
      backupEnabled: enabled,
      backupThresholdSecs: 2,
      loopPlayback: true,
      selectedAssetIds: buildPlaybackSelection(playback.selectedAssetIds, playback.playAllAssets),
    };
    setPlayback(nextPlayback);
    await savePlayback(nextPlayback, 'backupEnabled', { successMessage: 'Auto backup updated.' });
  };

  const toggleSelected = async (assetId: number, checked: boolean) => {
    const baseSelected = checked
      ? Array.from(new Set([...playback.selectedAssetIds, assetId]))
      : playback.selectedAssetIds.filter((id) => id !== assetId);
    const nextPlayback: MediaBoxConfig = {
      ...playback,
      selectedAssetIds: buildPlaybackSelection(baseSelected, playback.playAllAssets),
      loopPlayback: true,
    };
    if (!playback.playAllAssets && armEnabled && nextPlayback.selectedAssetIds.length === 0) {
      showSnackbar('At least one clip must remain selected while MediaBox is armed.', 'warning');
      return;
    }
    setPlayback(nextPlayback);
    await savePlayback(nextPlayback, 'selectedAssetIds', { suppressSuccess: true });
  };

  const readyAssetIds = React.useMemo(
    () => assets.filter((asset) => asset.normStatus === 'READY').map((asset) => asset.id),
    [assets],
  );
  const allReadyAssetsSelected = readyAssetIds.length > 0
    && readyAssetIds.every((assetId) => playback.selectedAssetIds.includes(assetId));

  const toggleSelectAll = async (checked: boolean) => {
    if (playback.playAllAssets) return;
    if (!checked && armEnabled) {
      showSnackbar('At least one clip must remain selected while MediaBox is armed.', 'warning');
      return;
    }
    const nextPlayback: MediaBoxConfig = {
      ...playback,
      selectedAssetIds: checked ? readyAssetIds : [],
      loopPlayback: true,
    };
    setPlayback(nextPlayback);
    await savePlayback(nextPlayback, 'selectedAssetIds', { suppressSuccess: true });
  };

  if (!activeChannel) {
    return (
      <Paper sx={{ p: 3, borderRadius: 0 }}>
        <Typography>Select a channel first.</Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (disabledMessage) {
    return (
      <Paper sx={{ p: 4, borderRadius: 0, border: '1px solid', borderColor: v('--border'), bgcolor: v('--bg-card') }}>
        <Stack spacing={2} alignItems="center">
          <Box sx={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: v('--border-strong'), bgcolor: v('--bg-input'), color: v('--text-muted') }}>
            <LockIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={900}>MediaBox</Typography>
          <Typography color="text.secondary">{disabledMessage}</Typography>
        </Stack>
      </Paper>
    );
  }

  const renderTopBar = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', borderBottom: '0.5px solid', borderColor: v('--border'), bgcolor: v('--bg-body') }}>
      <Box sx={{ minWidth: { xs: '100%', sm: 180 }, px: 2, py: 1.25, borderRight: { xs: 'none', sm: '0.5px solid' }, borderBottom: { xs: '0.5px solid', sm: 'none' }, borderColor: v('--border') }}>
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography sx={{ fontSize: '0.63rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Storage</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: 'text.secondary' }}>
              {bytesToGb(summary?.usedBytes || 0)} / {bytesToGb(summary?.storageBytes || 0)}
            </Typography>
          </Stack>
          <LinearProgress value={usagePercent} variant="determinate" sx={{ height: 4, borderRadius: 0 }} />
        </Stack>
      </Box>
      {[
        ['Clips', String(assets.length)],
        ['Armed', String(selectedCount)],
        ['Profile', '1080p · 25fps'],
        ['Status', runtimeAlert?.severity === 'success' ? 'Live via MediaBox' : playback.runtime?.armed ? 'Armed' : 'Idle'],
      ].map(([label, value], idx) => (
        <Box
          key={label}
          sx={{
            px: 2,
            py: 1.25,
            borderRight: { xs: idx % 2 === 0 ? '0.5px solid' : 'none', md: idx < 3 ? '0.5px solid' : 'none' },
            borderBottom: { xs: idx < 2 ? '0.5px solid' : 'none', md: 'none' },
            borderColor: v('--border'),
            minWidth: { xs: '50%', md: label === 'Status' ? 160 : 120 },
            flex: { xs: '1 1 50%', md: '0 0 auto' },
          }}
        >
          <Typography sx={{ fontSize: '0.63rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</Typography>
          <Typography
            sx={{
              fontSize: label === 'Status' ? '0.74rem' : label === 'Profile' ? '0.78rem' : '0.86rem',
              fontWeight: label === 'Status' ? 600 : 700,
              fontFamily: "'DM Mono', monospace",
              color:
                label === 'Status' && runtimeAlert?.severity === 'success'
                  ? 'success.main'
                  : 'text.primary',
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '0.5px', alignSelf: 'stretch', bgcolor: v('--border') }} />
      <Box sx={{ px: 2, py: 1.25, minWidth: { xs: '100%', md: 220 }, flex: 1, display: 'flex', alignItems: 'center', borderTop: { xs: '0.5px solid', md: 'none' }, borderColor: v('--border') }}>
        <Typography
          sx={{
            fontSize: '0.72rem',
            color:
              runtimeAlert?.severity === 'success'
                ? 'success.main'
                : runtimeAlert?.severity === 'error'
                  ? 'error.main'
                  : runtimeAlert?.severity === 'info'
                    ? 'info.main'
                    : 'text.secondary',
            fontFamily: "'DM Mono', monospace",
            whiteSpace: { xs: 'normal', md: 'nowrap' },
          }}
        >
          {runtimeAlert?.message || 'No active MediaBox event'}
        </Typography>
      </Box>
    </Box>
  );

  const renderToolbar = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 1.5, py: 1, borderBottom: '0.5px solid', borderColor: v('--border') }}>
      <Tooltip title={canUploadMore ? 'Upload media' : 'MediaBox is limited to four videos'}>
        <span>
        <IconButton component="label" disabled={!canUploadMore} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'primary.main', color: 'var(--on-primary)', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
          <CloudUploadIcon fontSize="small" />
          <input hidden multiple type="file" accept=".mp4,.mkv,.mov,video/mp4,video/x-matroska,video/quicktime" onChange={handleUpload} />
        </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Refresh">
        <IconButton onClick={() => load()} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: v('--border') }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}>
        <IconButton
          onClick={() => setViewMode((current) => (current === 'list' ? 'grid' : 'list'))}
          sx={{ borderRadius: 0, border: '0.5px solid', borderColor: v('--border') }}
        >
          {viewMode === 'list' ? <GridViewIcon fontSize="small" /> : <TableRowsIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem />
      <Tooltip title="This automatically prepares all clips for playback. If off, you will have to arm them manually after every upload.">
        <FormControlLabel
          control={<Switch size="small" checked={playback.playAllAssets} onChange={(event) => { void updateAutoArming(event.target.checked); }} />}
          label="Auto Arming"
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem', fontWeight: 800 } }}
        />
      </Tooltip>
      <Tooltip title="This enables auto playback of armed clips. Auto armed clips take over after two seconds of no live studio playback.">
        <FormControlLabel
          control={<Switch size="small" checked={playback.backupEnabled} onChange={(event) => { void updateAutoBackup(event.target.checked); }} />}
          label="Auto Backup"
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem', fontWeight: 800 } }}
        />
      </Tooltip>
      <Divider orientation="vertical" flexItem />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>Workflow</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={playback.uploadProfile}
        onChange={(_event, value) => { void updateUploadProfile(value as MediaBoxUploadProfile | null); }}
        disabled={uploading}
        aria-label="MediaBox audio workflow"
        sx={{ height: 32 }}
      >
        <ToggleButton
          value="h264_opus"
          disabled={assets.length > 0 && playback.uploadProfile !== 'h264_opus'}
          sx={{ px: 1.25, fontSize: '0.68rem', fontWeight: 800 }}
        >
          Opus
        </ToggleButton>
        <ToggleButton
          value="h264_aac"
          disabled={assets.length > 0 && playback.uploadProfile !== 'h264_aac'}
          sx={{ px: 1.25, fontSize: '0.68rem', fontWeight: 800 }}
        >
          AAC
        </ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title="About MediaBox workflow">
        <IconButton
          size="small"
          onClick={(event) => setWorkflowHelpAnchor(event.currentTarget)}
          aria-label="About MediaBox workflow"
          sx={{ width: 28, height: 28, border: '0.5px solid', borderColor: v('--border') }}
        >
          <HelpOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(workflowHelpAnchor?.isConnected)}
        anchorEl={workflowHelpAnchor}
        onClose={() => setWorkflowHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxWidth: 360, p: 1.5 } } }}
      >
        <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.55 }}>
          Choose the audio format required by your endpoint before uploading. Opus is used for WebRTC preview; AAC is used for HLS compatibility. To change workflow later, first delete every clip and empty this MediaBox.
        </Typography>
      </Popover>
      <Box sx={{ flex: 1 }} />
    </Box>
  );

  const renderDetailPanel = () => {
    if (!selectedAsset) return null;
    return (
      <Box
        sx={{
          width: { xs: '100%', lg: 260 },
          borderLeft: '0.5px solid',
          borderColor: v('--border'),
          bgcolor: v('--bg-body'),
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1.25, borderBottom: '0.5px solid', borderColor: v('--border') }}>
          <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 700, maxWidth: 180 }}>{selectedAsset.label}</Typography>
          <Tooltip title="Close details">
            <IconButton onClick={() => setSelectedAssetId(null)} sx={{ borderRadius: 0, width: 24, height: 24 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ aspectRatio: '16 / 9', bgcolor: v('--bg-input'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LazyThumbnail
            assetId={selectedAsset.id}
            label={selectedAsset.label}
            updatedAt={selectedAsset.updatedAt}
            src={thumbnailUrls[selectedAsset.id]}
            onVisible={ensureThumbnail}
            iconSize={28}
          />
        </Box>

        <Stack spacing={1.25} sx={{ p: 1.5, overflow: 'auto' }}>
          <TextField
            size="small"
            label="Clip name"
            value={renameDrafts[selectedAsset.id] ?? selectedAsset.label}
            onChange={(e) => setRenameDrafts((prev) => ({ ...prev, [selectedAsset.id]: e.target.value }))}
            InputProps={{ sx: { borderRadius: 0 } }}
          />
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Profile</Typography>
            <Typography sx={{ fontSize: '0.76rem', fontFamily: "'DM Mono', monospace" }}>
              {selectedAsset.width || 1920}×{selectedAsset.height || 1080} · {selectedAsset.fps || '25'}fps
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Codecs</Typography>
            <Typography sx={{ fontSize: '0.76rem', fontFamily: "'DM Mono', monospace" }}>
              {formatCodecPair(selectedAsset.videoCodec || 'h264', selectedAsset.audioCodec || 'aac')}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Size</Typography>
            <Typography sx={{ fontSize: '0.76rem', fontFamily: "'DM Mono', monospace" }}>
              {selectedAsset.fileSizeBytes ? bytesToGb(selectedAsset.fileSizeBytes) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Duration</Typography>
            <Typography sx={{ fontSize: '0.76rem', fontFamily: "'DM Mono', monospace" }}>
              {selectedAsset.durationSecs ? `${Math.round(selectedAsset.durationSecs)}s` : '--'}
            </Typography>
          </Box>
          {(selectedAsset.isCurrent || selectedAsset.isNext) && (
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Playback</Typography>
              <Typography sx={{ fontSize: '0.76rem', fontFamily: "'DM Mono', monospace", color: selectedAsset.isCurrent ? 'success.main' : 'info.main' }}>
                {selectedAsset.isCurrent ? 'Now playing' : 'Up next'}
              </Typography>
            </Box>
          )}
          {selectedAsset.normStatus !== 'READY' && (
            <Box>
              <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Readiness</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>
                {(selectedAsset.normStage || selectedAsset.normStatus).replace(/_/g, ' ')} · {getAssetProgress(selectedAsset)}%
              </Typography>
              <LinearProgress value={getAssetProgress(selectedAsset)} variant="determinate" sx={{ height: 6, borderRadius: 0 }} />
            </Box>
          )}
          {selectedAsset.normError && selectedAsset.normStatus === 'ERROR' && (
            <Alert severity="error" sx={{ borderRadius: 0 }}>
              {selectedAsset.normError}
            </Alert>
          )}
          <Divider />
          <Stack direction="row" spacing={0.75} justifyContent="space-between">
            <Tooltip title="Save clip name">
              <span>
                <IconButton
                  onClick={() => handleRename(selectedAsset)}
                  disabled={savingNameId === selectedAsset.id || String(renameDrafts[selectedAsset.id] || '').trim() === selectedAsset.label}
                  sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'primary.main', bgcolor: PRIMARY_SURFACE, color: 'primary.main' }}
                >
                  {savingNameId === selectedAsset.id ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant={playback.selectedAssetIds.includes(selectedAsset.id) || playback.playAllAssets ? 'contained' : 'outlined'}
              color="secondary"
              startIcon={<PlaylistPlayIcon fontSize="small" />}
              onClick={() => { void toggleSelected(selectedAsset.id, !(playback.selectedAssetIds.includes(selectedAsset.id) || playback.playAllAssets)); }}
              disabled={playback.playAllAssets}
              sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 900, minWidth: 112 }}
            >
              {playback.selectedAssetIds.includes(selectedAsset.id) || playback.playAllAssets ? 'Armed' : 'Arm Clip'}
            </Button>
            {selectedAsset.normStatus === 'ERROR' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={reprocessingId === selectedAsset.id ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                onClick={() => { void handleReprocess(selectedAsset.id); }}
                disabled={reprocessingId === selectedAsset.id}
                sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 900 }}
              >
                Re-process
              </Button>
            )}
            <Tooltip title="Delete clip">
              <span>
                <IconButton
                  color="error"
                  onClick={() => handleDelete(selectedAsset.id)}
                  disabled={deletingId === selectedAsset.id}
                  sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'error.main', color: 'error.main' }}
                >
                  {deletingId === selectedAsset.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderGrid = () => (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Checkbox
          size="small"
          checked={playback.playAllAssets || allReadyAssetsSelected}
          disabled={playback.playAllAssets || readyAssetIds.length === 0}
          onChange={(event) => { void toggleSelectAll(event.target.checked); }}
        />
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Select all ready clips</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
        {assets.map((asset) => {
        const isSelected = selectedAssetId === asset.id;
        const isArmed = playback.playAllAssets || playback.selectedAssetIds.includes(asset.id);
        const isCurrent = Boolean(asset.isCurrent);
        const isNext = Boolean(asset.isNext);
        const tone = getStatusTone(asset);
        const thumb = thumbnailUrls[asset.id];
        return (
          <Paper
            key={asset.id}
            onClick={() => setSelectedAssetId(asset.id)}
            sx={{
              borderRadius: 0,
              cursor: 'pointer',
              border: '0.5px solid',
              borderColor: isSelected ? 'secondary.main' : v('--border'),
              bgcolor: v('--bg-body'),
              overflow: 'hidden',
            }}
          >
            <Box sx={{ aspectRatio: '16 / 9', bgcolor: v('--bg-input'), position: 'relative' }}>
              <LazyThumbnail assetId={asset.id} label={asset.label} updatedAt={asset.updatedAt} src={thumb} onVisible={ensureThumbnail} />
              {isArmed && <Box sx={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, bgcolor: 'secondary.main' }} />}
            </Box>
            <Stack spacing={0.5} sx={{ p: 1, pr: 4.5, position: 'relative' }}>
              <Typography noWrap fontWeight={700} sx={{ fontSize: '0.78rem' }}>{asset.label}</Typography>
              <Typography noWrap sx={{ fontSize: '0.68rem', color: 'text.secondary', fontFamily: "'DM Mono', monospace" }}>
                {asset.fileSizeBytes ? bytesToGb(asset.fileSizeBytes) : '--'} · {asset.durationSecs ? `${Math.round(asset.durationSecs)}s` : '--'}
              </Typography>
              {(isCurrent || isNext) && (
                <Typography sx={{ fontSize: '0.66rem', color: isCurrent ? 'success.main' : 'info.main', textTransform: 'uppercase' }}>
                  {isCurrent ? 'Now playing' : 'Up next'}
                </Typography>
              )}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, bgcolor: tone.bg, color: tone.color, width: 'fit-content' }}>
                <Box sx={{ width: 6, height: 6, bgcolor: tone.color }} />
                <Typography sx={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>{tone.label} · {isArmed ? 'armed' : 'unarmed'}</Typography>
              </Box>
              {asset.normStatus === 'ERROR' && (
                <Tooltip title="Re-process clip">
                  <span>
                    <IconButton
                      color="warning"
                      onClick={(event) => { event.stopPropagation(); void handleReprocess(asset.id); }}
                      disabled={reprocessingId === asset.id}
                      sx={{ position: 'absolute', left: 6, bottom: 6, width: 24, height: 24, borderRadius: 0, border: '0.5px solid', borderColor: 'warning.main' }}
                    >
                      {reprocessingId === asset.id ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Delete clip">
                <span>
                  <IconButton
                    color="error"
                    onClick={(event) => { event.stopPropagation(); handleDelete(asset.id); }}
                    disabled={deletingId === asset.id}
                    sx={{ position: 'absolute', right: 6, bottom: 6, width: 24, height: 24, borderRadius: 0, border: '0.5px solid', borderColor: 'error.main' }}
                  >
                    {deletingId === asset.id ? <CircularProgress size={12} /> : <DeleteIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        );
        })}
      </Box>
    </Stack>
  );

  const renderList = () => (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr">
          {['', '', 'Name', 'Duration', 'Size', 'Status', ''].map((label, idx) => (
            <Box
              key={`${label}-${idx}`}
              component="th"
              sx={{
                textAlign: 'left',
                fontSize: '0.62rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'text.secondary',
                py: 1,
                px: 1,
                borderBottom: '0.5px solid',
                borderColor: v('--border'),
                '&:first-of-type': { pl: 0, width: 24 },
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {assets.map((asset) => {
          const isSelected = selectedAssetId === asset.id;
          const isArmed = playback.playAllAssets || playback.selectedAssetIds.includes(asset.id);
          const isCurrent = Boolean(asset.isCurrent);
          const isNext = Boolean(asset.isNext);
          const tone = getStatusTone(asset);
          const progressValue = getAssetProgress(asset);
          const thumb = thumbnailUrls[asset.id];
          return (
            <Box
              component="tr"
              key={asset.id}
              onClick={() => setSelectedAssetId(asset.id)}
              sx={{
                cursor: 'pointer',
                bgcolor: isSelected ? ROW_SELECTED_SURFACE : 'transparent',
                '&:hover': { bgcolor: isSelected ? ROW_SELECTED_SURFACE : ROW_HOVER_SURFACE },
              }}
            >
              <Box component="td" sx={{ py: 1, pr: 1, pl: 0, borderBottom: '0.5px solid', borderColor: v('--border-subtle') }}>
                <Checkbox
                  checked={isArmed}
                  disabled={playback.playAllAssets}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { void toggleSelected(asset.id, e.target.checked); }}
                  size="small"
                />
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle') }}>
                <Box sx={{ width: 72, height: 40, bgcolor: v('--bg-input'), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LazyThumbnail assetId={asset.id} label={asset.label} updatedAt={asset.updatedAt} src={thumb} onVisible={ensureThumbnail} iconSize={16} />
                </Box>
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle') }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{asset.label}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontFamily: "'DM Mono', monospace" }}>
                  {asset.width || 1920}×{asset.height || 1080} · {asset.fps || '25'}fps · {formatCodecPair(asset.videoCodec || 'h264', asset.audioCodec || 'aac')}
                </Typography>
                {(isCurrent || isNext) && (
                  <Typography sx={{ fontSize: '0.66rem', color: isCurrent ? 'success.main' : 'info.main', textTransform: 'uppercase' }}>
                    {isCurrent ? 'Now playing' : 'Up next'}
                  </Typography>
                )}
                {asset.normStatus !== 'READY' && (
                  <Box sx={{ width: 96, mt: 0.5 }}>
                    <LinearProgress value={progressValue} variant="determinate" sx={{ height: 4, borderRadius: 0 }} />
                  </Box>
                )}
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle'), fontFamily: "'DM Mono', monospace", fontSize: '0.72rem' }}>
                {asset.durationSecs ? `${Math.round(asset.durationSecs)}s` : '--'}
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle'), fontFamily: "'DM Mono', monospace", fontSize: '0.72rem' }}>
                {asset.fileSizeBytes ? bytesToGb(asset.fileSizeBytes) : '--'}
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle') }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, bgcolor: tone.bg, color: tone.color }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: tone.color }} />
                  <Typography sx={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>{tone.label} · {isArmed ? 'armed' : 'unarmed'}</Typography>
                </Box>
              </Box>
              <Box component="td" sx={{ py: 1, px: 1, borderBottom: '0.5px solid', borderColor: v('--border-subtle') }}>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Open details">
                    <IconButton onClick={(e) => { e.stopPropagation(); setSelectedAssetId(asset.id); }} sx={{ borderRadius: 0, width: 24, height: 24, border: '0.5px solid', borderColor: v('--border') }}>
                      <SaveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {asset.normStatus === 'ERROR' && (
                    <Tooltip title="Re-process clip">
                      <span>
                        <IconButton
                          color="warning"
                          onClick={(e) => { e.stopPropagation(); void handleReprocess(asset.id); }}
                          disabled={reprocessingId === asset.id}
                          sx={{ borderRadius: 0, width: 24, height: 24, border: '0.5px solid', borderColor: 'warning.main' }}
                        >
                          {reprocessingId === asset.id ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete clip">
                    <span>
                      <IconButton
                        color="error"
                        onClick={(e) => { e.stopPropagation(); handleDelete(asset.id); }}
                        disabled={deletingId === asset.id}
                        sx={{ borderRadius: 0, width: 24, height: 24, border: '0.5px solid', borderColor: 'error.main', color: 'error.main' }}
                      >
                        {deletingId === asset.id ? <CircularProgress size={12} /> : <DeleteIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Paper
      onDragEnter={(event) => {
        event.preventDefault();
        dragDepthRef.current += 1;
        if (canUploadMore && event.dataTransfer.types.includes('Files')) setDragActive(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = canUploadMore ? 'copy' : 'none';
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setDragActive(false);
        if (canUploadMore) void handleUploadFiles(Array.from(event.dataTransfer.files || []));
      }}
      sx={{ position: 'relative', borderRadius: 0, border: '0.5px solid', borderColor: dragActive ? 'primary.main' : v('--border'), bgcolor: v('--bg-card'), overflow: 'hidden' }}
    >
      {dragActive && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'grid', placeItems: 'center', bgcolor: 'rgba(var(--primary-rgb),0.12)', border: '2px dashed', borderColor: 'primary.main', pointerEvents: 'none' }}>
          <Stack alignItems="center" spacing={1} sx={{ p: 2, bgcolor: v('--bg-card'), border: '0.5px solid', borderColor: 'primary.main' }}>
            <CloudUploadIcon color="primary" />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800 }}>Drop clips to upload</Typography>
          </Stack>
        </Box>
      )}
      <Stack spacing={0}>
        {renderTopBar()}

        {(uploading || uploadState?.active) && (
          <Box sx={{ px: 2, py: 1.25, borderBottom: '0.5px solid', borderColor: v('--border'), bgcolor: INFO_SURFACE }}>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{uploadName || uploadState?.fileName || 'Uploading media'}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{uploading ? uploadPercent : (uploadState?.progress || 0)}%</Typography>
                  <Button size="small" variant="outlined" disabled={!uploadState?.active} onClick={handleCancelUpload} sx={{ borderRadius: 0, minWidth: 0, px: 1 }}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
              <LinearProgress value={uploading ? uploadPercent : (uploadState?.progress || 0)} variant="determinate" sx={{ height: 4, borderRadius: 0 }} />
            </Stack>
          </Box>
        )}

        {processingAssets.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, borderBottom: '0.5px solid', borderColor: v('--border'), bgcolor: INFO_SURFACE }}>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Readiness · {processingAssets.length} clip{processingAssets.length === 1 ? '' : 's'}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{readinessProgress}%</Typography>
                  <Button size="small" variant="outlined" onClick={handleCancelProcessing} sx={{ borderRadius: 0, minWidth: 0, px: 1 }}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
              <LinearProgress value={readinessProgress} variant="determinate" sx={{ height: 4, borderRadius: 0 }} />
            </Stack>
          </Box>
        )}

        {renderToolbar()}

        <Box sx={{ display: 'flex', minHeight: 560 }}>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 1.5 }}>
            {assets.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 220, px: 2, color: 'text.secondary' }}>
                <ErrorOutlineIcon sx={{ fontSize: 28 }} />
                <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5, maxWidth: 580 }}>
                  <Typography component="li" variant="body2">Upload up to 4 clips, not exceeding 1.2 GB in total.</Typography>
                  <Typography component="li" variant="body2">Once uploaded, ensure Auto Arming and Auto Backup are on.</Typography>
                  <Typography component="li" variant="body2">MediaBox will take over and play your uploaded clips in random order once your studio feed stops.</Typography>
                </Stack>
              </Stack>
            ) : viewMode === 'grid' ? renderGrid() : renderList()}
          </Box>

          {selectedAsset && renderDetailPanel()}
        </Box>
      </Stack>
    </Paper>
  );
};
